import React from 'react'
import {
  Box, Button, Card, CardContent, CircularProgress, FormControl,
  InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Pagination, Chip,
} from '@mui/material'
import { SportsEsports, Search } from '@mui/icons-material'
import { Layout } from '../components/Layout/Layout'
import apiClient from '../api'

interface IPurchase {
  _id: string
  receiptId: string
  amazonUserId: string
  assignedLevel: number
  price: number
  purchaseDate: number
  createdAt?: string
}

export const ColorBlockJamDashboard: React.FC = () => {
  const today = new Date().toISOString().split('T')[0]
  const thirtyAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000)
    .toISOString()
    .split('T')[0]

  const [startDate, setStartDate] = React.useState(thirtyAgo)
  const [endDate, setEndDate] = React.useState(today)
  const [assignedLevel, setAssignedLevel] = React.useState<string>('Any')
  const [minPrice, setMinPrice] = React.useState('')
  const [maxPrice, setMaxPrice] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [purchases, setPurchases] = React.useState<IPurchase[]>([])

  const [page, setPage] = React.useState(1)
  const limit = 20
  const currentPageData = React.useMemo(() => {
    const start = (page - 1) * limit
    return purchases.slice(start, start + limit)
  }, [purchases, page])

  const getData = async () => {
    try {
      setLoading(true)
      setError('')
      const qs = new URLSearchParams()
      if (startDate) qs.append('startDate', String(+new Date(startDate) / 1000))
      if (endDate) qs.append('endDate', String(+new Date(endDate) / 1000))
      if (assignedLevel !== 'Any') qs.append('assignedLevel', assignedLevel)
      if (minPrice) qs.append('minPrice', minPrice)
      if (maxPrice) qs.append('maxPrice', maxPrice)

      const { data } = await apiClient.get(`/color-block-jam/purchases?${qs}`)
      setPurchases(data.result.purchases || [])
    } catch (e) {
      setError('Failed to fetch purchases')
    } finally {
      setLoading(false)
      setPage(1)
    }
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SportsEsports sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>
                Color-Block-Jam
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Track one-time purchase events and revenue analytics
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Search sx={{ color: '#ec4899', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>
                Filter Purchases
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              sx={{ flexWrap: 'wrap' }}
            >
              <TextField
                type="date"
                size="small"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Level</InputLabel>
                <Select
                  label="Level"
                  value={assignedLevel}
                  onChange={(e: SelectChangeEvent) =>
                    setAssignedLevel(e.target.value)
                  }
                >
                  <MenuItem value="Any">Any</MenuItem>
                  {[1, 2, 3, 4, 5].map((lvl) => (
                    <MenuItem key={lvl} value={String(lvl)}>
                      {lvl}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Min Price"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              <TextField
                size="small"
                label="Max Price"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
              <Button variant="contained" onClick={getData}>
                Get Data
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {loading && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CircularProgress size={20} /> <Typography>Loading…</Typography>
          </Stack>
        )}
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Purchase Date</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Receipt ID</TableCell>
                  <TableCell>Amazon User ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No purchases found. Adjust filters and click "Get Data".
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentPageData.map((p) => (
                    <TableRow key={p._id} hover>
                      <TableCell>
                        {new Date(p.purchaseDate).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Level ${p.assignedLevel}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          ${p.price?.toFixed?.(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {p.receiptId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {p.amazonUserId}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Typography variant="body2">
            Page {page} / {Math.max(1, Math.ceil(purchases.length / limit))}
          </Typography>
          <Pagination
            count={Math.ceil(purchases.length / limit)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Stack>
      </Box>
    </Layout>
  )
}